<template>
  <div class="user-table-wrap">
    <div class="table-topbar">
      <p class="table-kicker">Listado de usuarios</p>
      <div class="input-group search-box">
        <span class="input-group-text"><i class="bi bi-search"></i></span>
        <input class="form-control" type="text" placeholder="Buscar usuario o nombre" v-model="busqueda" @input="Filtro" />
        <button v-if="busqueda" class="btn btn-outline-secondary" type="button" @click="limpiarBusqueda" title="Limpiar búsqueda">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
    </div>

    <div class="table-responsive mt-2">
      <table class="table table-bordered table-hover align-middle qh-admin-table">
        <thead class="reducirTamaño">
          <tr class="text-center text-nowrap">
            <th class="sortable-th" @click="sortBy('idUsuario')" role="button" tabindex="0" @keydown.enter="sortBy('idUsuario')">ID<i class="bi ms-1" :class="sortIcon('idUsuario')"></i></th>
            <th class="sortable-th" @click="sortBy('usuNombres')" role="button" tabindex="0" @keydown.enter="sortBy('usuNombres')">APELLIDOS Y NOMBRES<i class="bi ms-1" :class="sortIcon('usuNombres')"></i></th>
            <th class="sortable-th" @click="sortBy('usuCodigo')" role="button" tabindex="0" @keydown.enter="sortBy('usuCodigo')">USUARIO<i class="bi ms-1" :class="sortIcon('usuCodigo')"></i></th>
            <th>CONTRASEÑA</th>
            <th class="sortable-th" @click="sortBy('audfecCreacion')" role="button" tabindex="0" @keydown.enter="sortBy('audfecCreacion')">CREACIÓN<i class="bi ms-1" :class="sortIcon('audfecCreacion')"></i></th>
            <th class="sortable-th" @click="sortBy('audfecActualizacion')" role="button" tabindex="0" @keydown.enter="sortBy('audfecActualizacion')">ACTUALIZACIÓN<i class="bi ms-1" :class="sortIcon('audfecActualizacion')"></i></th>
            <th>ESTADO</th>
            <th><i class="bi bi-boxes fs-5"></i></th>
          </tr>
        </thead>

        <tbody class="text-nowrap text-center texto-fini align-middle">
          <tr v-if="loading">
            <td colspan="8" class="table-state-cell">
              <div class="state-block">
                <div class="spinner-border text-warning" style="width: 2.5rem; height: 2.5rem;"></div>
                <div class="mt-3 text-secondary fw-semibold">Filtrando información...</div>
              </div>
            </td>
          </tr>

          <tr v-else-if="!paginatedLista.length">
            <td colspan="8" class="table-state-cell">
              <div class="state-block empty">
                <i class="bi bi-search fs-1 text-secondary"></i>
                <div class="mt-2 fw-semibold">No se encontraron usuarios</div>
                <small class="text-muted">Prueba con otro criterio de búsqueda.</small>
              </div>
            </td>
          </tr>

          <tr v-else v-for="lista in paginatedLista" :key="lista.idUsuario" class="reducirTamaño">
            <td>{{ lista.idUsuario }}</td>
            <td class="text-start">{{ lista.usuNombres }}</td>
            <td>{{ lista.usuCodigo }}</td>
            <td>
              <div class="password-toggle-cell">
                <span class="password-text font-monospace">
                  {{ isPasswordVisible(lista.idUsuario) ? getPasswordText(lista) : '••••••••' }}
                </span>
                <button 
                  type="button"
                  class="btn btn-sm btn-link text-secondary p-0 ms-1 eye-btn"
                  @click="togglePasswordVisibility(lista.idUsuario)"
                  :title="isPasswordVisible(lista.idUsuario) ? 'Ocultar contraseña' : 'Ver contraseña'"
                >
                  <i :class="isPasswordVisible(lista.idUsuario) ? 'bi bi-eye-slash-fill text-primary' : 'bi bi-eye'"></i>
                </button>
              </div>
            </td>
            <td>{{ lista.audfecCreacion }}</td>
            <td>{{ lista.audfecActualizacion }}</td>
            <td>
              <button 
                type="button"
                class="status-toggle-btn" 
                :class="isUserActive(lista) ? 'active' : 'inactive'"
                :disabled="updatingStatusId === lista.idUsuario"
                @click="toggleEstado(lista)"
                :title="isUserActive(lista) ? 'Usuario Activo - Clic para desactivar' : 'Usuario Inactivo - Clic para activar'"
              >
                <span class="toggle-slider">
                  <i :class="isUserActive(lista) ? 'bi bi-check-lg' : 'bi bi-x-lg'"></i>
                </span>
                <span class="toggle-label">{{ isUserActive(lista) ? 'Activo' : 'Inactivo' }}</span>
              </button>
            </td>
            <td>
              <div class="user-actions">
                <button @click="openEditModal(lista.idPerfil, lista.perNombre, lista)" class="btn btn-warning action-pill" title="Editar perfil del usuario"><i class="bi bi-pencil text-white fs-5"></i></button>
                <button @click="eliminarUsuario(lista)" class="btn btn-light text-danger action-pill" title="Eliminar usuario"><i class="bi bi-trash3 fs-5"></i></button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="table-footer-bar">
      <div class="page-size-control">
        <label>Mostrar</label>
        <select v-model.number="itemsPerPage" @change="onPageSizeChange" class="form-select form-select-sm">
          <option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}</option>
        </select>
        <span>registros</span>
      </div>
      <div class="record-count">{{ totalRows }} resultado(s)</div>
    </div>

    <div class="pagination-container d-flex justify-content-center align-items-center gap-2 my-2">
      <button class="btn btn-outline-primary btn-sm" @click="currentPage = 1" :disabled="currentPage === 1"><i class="bi bi-chevron-double-left"></i></button>
      <button class="btn btn-outline-primary btn-sm" @click="currentPage--" :disabled="currentPage === 1"><i class="bi bi-chevron-left"></i></button>
      <span class="pagination-info small">Página {{ currentPage }} de {{ totalPages || 1 }}</span>
      <button class="btn btn-outline-primary btn-sm" @click="currentPage++" :disabled="currentPage === totalPages || totalPages === 0"><i class="bi bi-chevron-right"></i></button>
      <button class="btn btn-outline-primary btn-sm" @click="currentPage = totalPages" :disabled="currentPage === totalPages || totalPages === 0"><i class="bi bi-chevron-double-right"></i></button>
    </div>

    <b-modal ref="editModal" centered hide-footer hide-close-button dialog-class="modal-md">
      <template #modal-title>
        <div class="d-flex align-items-center gap-2">
          <i class="bi bi-person-gear fs-4 text-primary"></i>
          <span class="fw-semibold">Editar perfil del usuario</span>
        </div>
      </template>

      <div class="container-fluid">
        <div class="mb-3">
          <label class="form-label fw-semibold">Seleccionar perfil</label>
          <div class="input-group">
            <span class="input-group-text bg-light"><i class="bi bi-person-badge"></i></span>
            <select class="form-select" v-model="idPerfil">
              <option value="" disabled>-- Seleccionar perfil --</option>
              <option v-for="item in ListaPerfil" :key="item.idPerfil" :value="item.idPerfil">{{ item.perNombre }}</option>
            </select>
          </div>
        </div>

        <div class="d-flex justify-content-end mt-4 gap-2">
          <b-button variant="success" class="px-4" @click="saveChanges"><i class="bi bi-check-circle me-1"></i> Guardar</b-button>
          <b-button variant="outline-secondary" class="px-4" @click="closeEditModal"><i class="bi bi-x-lg me-1"></i> Cerrar</b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import tableUx from "@/mixins/tableUx";
import { mapGetters } from "vuex";
import Swal from "sweetalert";
import Swal2 from "sweetalert2";

export default {
  mixins: [tableUx],
  name: "PowerbyTablaUsuario",
  data() {
    return {
      busqueda: "",
      loading: false,
      currentPage: 1,
      itemsPerPage: 10,
      idPerfil: "",
      nombrePerfil: "",
      listaUsuarios: [],
      idUsuario: "",
      idUsuarioUnico: "",
      updatingStatusId: null,
      showPasswordMap: {},
    };
  },
  computed: {
    ...mapGetters("acciones", ["ListaPerfil", "ListaUsuarios"]),
    filteredList() {
      const filtro = (this.busqueda || "").toLowerCase().trim();
      if (!filtro) return this.ListaUsuarios;
      return this.ListaUsuarios.filter(item =>
        item.usuNombres.toLowerCase().includes(filtro) || item.usuCodigo.toLowerCase().includes(filtro)
      );
    },
    totalRows() { return this.filteredList.length; },
    totalPages() { return Math.ceil(this.filteredList.length / this.itemsPerPage); },
    paginatedLista() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.sortedRows(this.filteredList).slice(start, start + this.itemsPerPage);
    }
  },
  mounted() { this.idUsuario = localStorage.getItem("userId"); },
  methods: {
    isPasswordVisible(id) {
      return !!this.showPasswordMap[id];
    },
    togglePasswordVisibility(id) {
      const actual = !!this.showPasswordMap[id];
      this.$set(this.showPasswordMap, id, !actual);
    },
    getPasswordText(usuario) {
      if (!usuario) return '••••••••';
      console.log('Datos del usuario en fila:', usuario);
      const pass = usuario.usuContrasena ?? 
                   usuario.usuContraseña ?? 
                   usuario.usuPassword ?? 
                   usuario.usuPass ?? 
                   usuario.usuClave ?? 
                   usuario.contrasena ?? 
                   usuario.contraseña ?? 
                   usuario.password;
      return (pass !== null && pass !== undefined && pass !== '') ? pass : '(Sin contraseña)';
    },
    isUserActive(usuario) {
      // Según requerimiento: usuEstadoUsuario === false (o 0) significa ACTIVADO
      // usuEstadoUsuario === true (o 1) significa DESACTIVADO
      if (usuario.usuEstadoUsuario !== undefined && usuario.usuEstadoUsuario !== null) {
        const val = usuario.usuEstadoUsuario;
        return val === false || val === 0 || val === "0" || val === "false";
      }
      if (usuario.usuEstado !== undefined && usuario.usuEstado !== null) {
        const val = usuario.usuEstado;
        return val === false || val === 0 || val === "0" || val === "false";
      }
      return true;
    },
    async toggleEstado(usuario) {
      this.updatingStatusId = usuario.idUsuario;
      const estaActivoActualmente = this.isUserActive(usuario);
      const pasaraAActivo = !estaActivoActualmente;

      try {
        const response = await this.axios.put(`BIClientes/ModificarEstadoUsuario?idUsuario=${usuario.idUsuario}`);
        const resData = response.data || {};
        
        const esExito = resData.exito === true || resData.exito === "true" || response.status === 200;

        if (esExito) {
          // 1. Actualizar reactivamente en la fila local:
          // Si pasará a Activo -> usuEstadoUsuario = false (0)
          // Si pasará a Inactivo -> usuEstadoUsuario = true (1)
          const nuevoValorDb = pasaraAActivo ? false : true;
          this.$set(usuario, "usuEstadoUsuario", nuevoValorDb);
          this.$set(usuario, "usuEstado", pasaraAActivo ? 0 : 1);

          // 2. Disparar recarga en store
          this.$store.dispatch("acciones/ListarUsuarios");

          // 3. Alertas personalizadas según la acción resultante
          const swalObj = this.$swal && typeof this.$swal.fire === "function" ? this.$swal : null;

          if (pasaraAActivo) {
            if (swalObj) {
              swalObj.fire({
                icon: "success",
                title: "Usuario activado",
                text: `Se procedió a activar al usuario ${usuario.usuNombres || ''}.`,
                timer: 2000,
                showConfirmButton: false,
              });
            } else {
              Swal("Usuario activado", `Se procedió a activar al usuario ${usuario.usuNombres || ''}.`, "success");
            }
          } else {
            if (swalObj) {
              swalObj.fire({
                icon: "warning",
                title: "Usuario desactivado",
                text: `Se procedió a desactivar al usuario ${usuario.usuNombres || ''}.`,
                timer: 2000,
                showConfirmButton: false,
              });
            } else {
              Swal("Usuario desactivado", `Se procedió a desactivar al usuario ${usuario.usuNombres || ''}.`, "warning");
            }
          }
        } else {
          throw new Error(resData.mensaje || "No se pudo modificar el estado.");
        }
      } catch (error) {
        console.error("Error al modificar estado:", error);
        const message = error.response?.data?.mensaje || error.message || "Error al intentar modificar el estado.";
        if (this.$swal && typeof this.$swal.fire === "function") {
          this.$swal.fire({ icon: "error", title: "Error", text: message });
        } else {
          Swal("Error", message, "error");
        }
      } finally {
        this.updatingStatusId = null;
      }
    },
    async eliminarUsuario(usuario) {
      // Usar SweetAlert2 explícitamente para modales con HTML
      Swal2.fire({
        title: "Obteniendo información...",
        text: "Consultando accesos y perfiles asociados...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal2.showLoading();
        }
      });

      let infoEliminacion = { tieneDatos: false, perfil: null, categorias: [] };

      try {
        const resInfo = await this.axios.get(`BIClientes/ObtenerInfoEliminacion/${usuario.idUsuario}`);
        console.log("Respuesta ObtenerInfoEliminacion:", resInfo.data);
        if (resInfo && resInfo.data) {
          infoEliminacion = resInfo.data;
        }
      } catch (err) {
        console.warn("No se pudo obtener detalle previo de eliminación:", err);
      }

      // 2. Armar el HTML del diálogo de confirmación según la información recibida
      let htmlContent = `<p style="font-size: 0.95rem; margin-bottom: 0.8rem;">¿Estás seguro de que deseas eliminar permanentemente al usuario <strong>${usuario.usuNombres || ''}</strong>?</p>`;
      
      const tieneDatos = infoEliminacion.tieneDatos === true || infoEliminacion.tieneDatos === "true" || !!infoEliminacion.perfil;

      if (tieneDatos) {
        let categoriasHtml = '';
        if (infoEliminacion.categorias && infoEliminacion.categorias.length) {
          const catList = infoEliminacion.categorias.map(c => `<li style="padding: 2px 0;"><i class="bi bi-folder2-open text-primary me-1"></i>${c}</li>`).join('');
          categoriasHtml = `
            <div style="text-align: left; background: #f8f9fa; border: 1px solid #e9ecef; padding: 10px 14px; border-radius: 8px; margin-top: 8px;">
              <small class="text-muted d-block mb-1"><strong>Categorías asociadas:</strong></small>
              <ul style="margin: 0; padding-left: 1.2rem; font-size: 0.85rem; max-height: 120px; overflow-y: auto; list-style-type: none;">
                ${catList}
              </ul>
            </div>
          `;
        }

        htmlContent += `
          <div style="background: #fff3cd; border: 1px solid #ffeeba; border-radius: 8px; padding: 12px; text-align: left; color: #856404;">
            <div style="font-weight: 600; font-size: 0.9rem; margin-bottom: 4px;">
              <i class="bi bi-exclamation-triangle-fill text-warning me-1"></i> Perfil asignado: <span class="badge bg-dark">${infoEliminacion.perfil || 'Sin perfil'}</span>
            </div>
            <p style="font-size: 0.82rem; margin: 0;">Al confirmar, se eliminarán permanentemente sus accesos a este perfil y sus categorías relacionadas.</p>
            ${categoriasHtml}
          </div>
        `;
      } else {
        htmlContent += `
          <div style="background: #e2e3e5; border: 1px solid #d6d8db; border-radius: 8px; padding: 10px; text-align: left; color: #383d41; font-size: 0.85rem;">
            <i class="bi bi-info-circle-fill me-1"></i> ${infoEliminacion.mensaje || 'El usuario no tiene perfiles ni categorías asociadas actualmente.'}
          </div>
        `;
      }

      // 3. Solicitar confirmación con SweetAlert2
      const result = await Swal2.fire({
        icon: "warning",
        title: "¿Eliminar usuario completamente?",
        html: htmlContent,
        showCancelButton: true,
        confirmButtonText: "<i class='bi bi-trash3-fill me-1'></i> Sí, eliminar usuario",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#dc3545",
        cancelButtonColor: "#6c757d",
        focusCancel: true
      });

      if (!result.isConfirmed) return;

      // 4. Ejecutar el endpoint de eliminación completa
      try {
        const response = await this.axios.delete(`BIClientes/EliminarUsuarioCompleto/${usuario.idUsuario}`);
        const data = response.data || {};
        
        const esExito = data.exito === true || data.exito === "true" || response.status === 200;

        if (esExito) {
          await this.$store.dispatch("acciones/ListarUsuarios");
          Swal2.fire({
            icon: "success",
            title: "Usuario eliminado",
            text: data.mensaje || "El usuario y todos sus accesos fueron eliminados correctamente.",
            timer: 2200,
            showConfirmButton: false,
          });
        } else {
          throw new Error(data.mensaje || "No se pudo completar la eliminación del usuario.");
        }
      } catch (error) {
        console.error("Error al eliminar usuario completo:", error);
        const message = error.response?.data?.mensaje || error.response?.data?.message || error.message || "No se pudo eliminar el usuario.";
        Swal2.fire({
          icon: "error",
          title: "No se pudo eliminar",
          text: message
        });
      }
    },
    limpiarBusqueda() { this.busqueda = ""; this.currentPage = 1; },
    openEditModal(idPerfil, nombre, id) {
      this.idPerfil = idPerfil;
      this.nombrePerfil = nombre;
      this.idUsuarioUnico = id.idUsuario;
      this.$refs.editModal.show();
    },
    closeEditModal() {
      this.idPerfil = "";
      this.nombrePerfil = "";
      this.$refs.editModal.hide();
    },
    saveChanges() {
      const from = {
        idUsuario: this.idUsuarioUnico,
        idPerfil: this.idPerfil,
        idUsuCreacion: this.idUsuario,
      };
      this.axios.put(`BIClientes/ModificarAcceso`, from).then(() => {
        Swal(`Se realizaron los cambios`, "", "success");
        this.closeEditModal();
      });
    },
    Filtro() {
      this.loading = true;
      setTimeout(() => {
        this.currentPage = 1;
        this.loading = false;
      }, 300);
    },
  },
};
</script>

<style scoped>
.b-modal .modal-header button.close { display: none; }
.user-table-wrap { display: grid; gap: .85rem; }
.table-topbar { display: flex; justify-content: space-between; align-items: center; gap: 1rem; flex-wrap: wrap; }
.table-kicker { margin: 0; color: #5f6f84; font-size: 1rem; }
.search-box { max-width: 430px; }
.search-box .form-control, .search-box .input-group-text, .search-box .btn { min-height: 52px; }
.qh-admin-table thead tr { background: #eef4fb; color: #234267; }
.qh-admin-table thead th { vertical-align: middle; }
.action-pill { border-radius: 12px; padding: .35rem .8rem; }.user-actions{display:inline-flex;gap:.4rem;align-items:center}
.table-state-cell { height: 220px; }
.state-block { min-height: 220px; display: flex; flex-direction: column; align-items: center; justify-content: center; }
.state-block.empty { color: #5f6f84; }
.reducirTamaño { font-size: .72rem; }

/* Switch Toggle Animado para Estado de Usuario */
.status-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 3px 10px 3px 4px;
  border-radius: 30px;
  border: none;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 600;
  transition: background-color 0.3s ease, box-shadow 0.2s ease, transform 0.1s ease;
  user-select: none;
}
.status-toggle-btn:hover {
  transform: scale(1.03);
}
.status-toggle-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.status-toggle-btn.active {
  background-color: #e6f4ea;
  color: #1e7e34;
  border: 1px solid #b7e1cd;
}
.status-toggle-btn.active .toggle-slider {
  background-color: #28a745;
  color: #ffffff;
  transform: translateX(0);
}

.status-toggle-btn.inactive {
  background-color: #fce8e6;
  color: #d93025;
  border: 1px solid #f5c6cb;
}
.status-toggle-btn.inactive .toggle-slider {
  background-color: #dc3545;
  color: #ffffff;
  transform: translateX(0);
}

.toggle-slider {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  transition: background-color 0.3s ease, transform 0.3s ease;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.toggle-label {
  letter-spacing: 0.3px;
}

/* Estilos de la celda de Contraseña */
.password-toggle-cell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
}
.password-text {
  letter-spacing: 2px;
  font-size: 0.85rem;
}
.eye-btn {
  font-size: 1rem;
  line-height: 1;
  transition: transform 0.15s ease;
}
.eye-btn:hover {
  transform: scale(1.15);
}

@media (max-width: 767.98px) {
  .table-topbar { align-items: stretch; }
  .search-box { max-width: 100%; width: 100%; }
}
</style>
